import "react-international-phone/style.css";

import {
  InputAdornment,
  ListSubheader,
  MenuItem,
  Select,
  TextField,
  Typography,
} from "@mui/material";
import { get } from "lodash-es";
import { useMemo, useState } from "react";
import {
  type Control,
  Controller,
  type FieldError,
  type FieldErrors,
  type FieldPath,
  type FieldValues,
} from "react-hook-form";
import {
  type CountryIso2,
  defaultCountries,
  FlagImage,
  parseCountry,
  usePhoneInput,
} from "react-international-phone";

import { Resources, useResource } from "Infrastructure/Translations/Resources";
import { InputWrapper } from "Shared/Inputs/InputWrapper";

type BlPhoneInputProps<TForm extends FieldValues> = {
  name: FieldPath<TForm>;
  control: Control<TForm>;
  errors: FieldErrors<TForm>;
  label?: string;
  required?: boolean;
  defaultCountryCode?: string;
  placeholder?: string;
};

type PhoneFieldProps = {
  value: string;
  onChange: (phone: string) => void;
  onBlur: () => void;
  fieldRef: React.Ref<HTMLInputElement>;
  defaultCountryCode: string;
  placeholder?: string;
  hasError: boolean;
};

const FLAG_SIZE = 20;

const PhoneField = ({
  value,
  onChange,
  onBlur,
  fieldRef,
  defaultCountryCode,
  placeholder,
  hasError,
}: PhoneFieldProps): React.ReactNode => {
  const { t } = useResource();
  const [search, setSearch] = useState("");

  const phoneInput = usePhoneInput({
    defaultCountry: defaultCountryCode.toLowerCase() as CountryIso2,
    value: value || "",
    countries: defaultCountries,
    onChange: (data) => {
      onChange(data.phone);
    },
  });

  const parsedCountries = useMemo(() => defaultCountries.map(parseCountry), []);

  const filteredCountries = useMemo(() => {
    if (!search) return parsedCountries;
    const lower = search.toLowerCase();
    return parsedCountries.filter(
      (c) =>
        c.name.toLowerCase().includes(lower) ||
        c.dialCode.includes(search) ||
        c.iso2.includes(lower),
    );
  }, [search, parsedCountries]);

  return (
    <TextField
      value={phoneInput.inputValue}
      onChange={phoneInput.handlePhoneValueChange}
      onBlur={onBlur}
      inputRef={(node) => {
        phoneInput.inputRef.current = node;
        if (typeof fieldRef === "function") {
          fieldRef(node);
        } else if (fieldRef && "current" in fieldRef) {
          (
            fieldRef as React.MutableRefObject<HTMLInputElement | null>
          ).current = node;
        }
      }}
      type="tel"
      placeholder={placeholder}
      error={hasError}
      fullWidth
      size="small"
      slotProps={{
        input: {
          startAdornment: (
            <InputAdornment position="start" sx={{ mr: "2px", ml: "-8px" }}>
              <Select
                MenuProps={{
                  autoFocus: false,
                  style: {
                    height: "300px",
                    width: "360px",
                    top: "10px",
                    left: "-34px",
                  },
                  transformOrigin: {
                    vertical: "top",
                    horizontal: "left",
                  },
                }}
                sx={{
                  width: "max-content",
                  // Hide all borders/outlines — no focus ring
                  fieldset: { display: "none" },
                  "&.Mui-focused fieldset": { display: "none" },
                  ".MuiSelect-select": {
                    padding: "8px",
                    paddingRight: "24px !important",
                  },
                  ".MuiSelect-select:focus": {
                    backgroundColor: "transparent",
                  },
                  svg: { right: 0 },
                }}
                value={phoneInput.country.iso2}
                onChange={(e) =>
                  phoneInput.setCountry(e.target.value as CountryIso2)
                }
                onOpen={() => setSearch("")}
                renderValue={(selected) => (
                  <FlagImage
                    iso2={selected}
                    style={{
                      display: "flex",
                      width: FLAG_SIZE,
                      height: FLAG_SIZE,
                    }}
                  />
                )}
              >
                <ListSubheader sx={{ px: 1, py: 1 }}>
                  <TextField
                    size="small"
                    autoFocus
                    placeholder={t(Resources.Common.Search)}
                    fullWidth
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    onKeyDown={(e) => e.stopPropagation()}
                  />
                </ListSubheader>
                {filteredCountries.map((countryData) => (
                  <MenuItem key={countryData.iso2} value={countryData.iso2}>
                    <FlagImage
                      iso2={countryData.iso2}
                      style={{ marginRight: "8px", width: FLAG_SIZE }}
                    />
                    <Typography marginRight="8px">
                      {countryData.name}
                    </Typography>
                    <Typography color="gray">
                      +{countryData.dialCode}
                    </Typography>
                  </MenuItem>
                ))}
              </Select>
            </InputAdornment>
          ),
        },
      }}
    />
  );
};

export const BlPhoneInput = <TForm extends FieldValues>({
  name,
  control,
  errors,
  label,
  required,
  defaultCountryCode = "cz",
  placeholder,
}: BlPhoneInputProps<TForm>): React.ReactNode => {
  const fieldError = get(errors, name as string) as FieldError | undefined;

  return (
    <Controller
      control={control}
      name={name}
      render={({ field: { onChange, value, onBlur, ref } }) => (
        <InputWrapper
          errorProps={{ error: fieldError }}
          labelProps={label ? { label, required } : undefined}
        >
          <PhoneField
            value={value}
            onChange={onChange}
            onBlur={onBlur}
            fieldRef={ref}
            defaultCountryCode={defaultCountryCode}
            placeholder={placeholder}
            hasError={!!fieldError}
          />
        </InputWrapper>
      )}
    />
  );
};
